"use client";

import type { FC, ReactNode } from "react";
import { AppImage } from "@/src/components/NextImage";
import { useState } from "react";
import { cn } from "utils";
import { AUTH_H5_LAYOUT, SIGN_IN_PAGE_ASSETS } from "../authLayoutTokens";

interface VerifyEmailPageLayoutProps {
  children: ReactNode;
  className?: string;
  /** 键盘弹起时收起底部装饰，释放可视高度（Android WebView resize 等）。 */
  collapseDecor?: boolean;
}

/** Figma 96154:99635 — decor BG/Image height (489→852). Hidden on md+ (PC Sign in shell). */
const VERIFY_EMAIL_DECOR_HEIGHT_PX = 363;

/**
 * UI2 VerifyEmail 扁平全页（Figma 96154:99635，D19）。
 * H5：顶对齐内容列 + 底部大图装饰。
 * PC（md+）：居中列对齐 Sign in PC `96344:111201`，无底部 decor（design D3 fallback）。
 */
export const VerifyEmailPageLayout: FC<VerifyEmailPageLayoutProps> = ({
  children,
  className,
  collapseDecor = false,
}) => {
  const [bgMissing, setBgMissing] = useState(false);

  return (
    <div
      className={cn(
        "flex h-dvh w-full flex-col overflow-hidden bg-background_solid_neutral_secondary",
        className,
      )}
      data-testid="verify-email-page-layout"
      data-figma-node="96154:99635"
      data-figma-node-pc="96344:111201"
      data-figma-fallback="sign-in-pc-shell"
    >
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col px-5 md:justify-center",
          AUTH_H5_LAYOUT.verifyEmailContentTopClass,
        )}
        data-testid="verify-email-content-shell"
      >
        <div
          className={cn(
            "mx-auto flex w-full max-w-[353px] flex-col",
            "md:max-w-none md:flex-none md:gap-8",
          )}
          data-testid="verify-email-content-column"
        >
          <header
            className="flex w-full max-w-[330px] shrink-0 flex-col items-start md:gap-[21px]"
            data-testid="verify-email-header"
          >
            <AppImage
              src={SIGN_IN_PAGE_ASSETS.logo}
              alt="EdWealth"
              width={60}
              height={32}
              loading="eager"
              className="h-8 w-auto shrink-0 self-start"
              data-testid="verify-email-brand-logo"
            />
          </header>
          <div className="mt-[21px] flex shrink-0 flex-col md:mt-0 md:flex-none">
            {children}
          </div>
        </div>
      </div>
      <div
        className="relative shrink-0 overflow-hidden transition-all duration-200 md:hidden"
        style={{
          height: collapseDecor ? 0 : VERIFY_EMAIL_DECOR_HEIGHT_PX,
        }}
        aria-hidden
        data-testid="verify-email-decor"
        data-collapsed={collapseDecor ? "true" : "false"}
      >
        {bgMissing ? (
          <div className="h-full w-full bg-gradient-to-t from-background_brand_overlay10 to-transparent" />
        ) : (
          <AppImage
            src={SIGN_IN_PAGE_ASSETS.verifyEmailBg}
            alt=""
            width={393}
            height={363}
            className="absolute inset-0 h-full w-full object-cover object-bottom"
            onError={() => setBgMissing(true)}
          />
        )}
      </div>
    </div>
  );
};
